/**
 * The account's plan status, collapsed to the FOUR values a staff viewer actually needs — on the
 * principal's instruction (2026-07-15): "แสดงอะไรไม่รู้เยอะแยะเกินไป ... เอาแบบง่ายๆพอ". The portal is a
 * read-only monitor for staff, most of them Viewers; the seven-way plan_state vocabulary
 * (paid_expired / workaround_trial / lapsed_trial / …) and its essay-length badges were noise on that
 * screen. The nuance is not lost — it still lives in the backend `v_plan_incident` view and the
 * $102,831 desktop deliverable — it is just not what a monitoring UI leads with.
 *
 *   active    plan is running                         ← live, workaround_trial (genuinely active)
 *   expired   plan has ended                          ← paid_expired, lapsed_trial, cancelled, expired_other
 *   free      no purchased plan on record             ← unknown + no base_type (the ~892 Free/Basic accounts)
 *   unknown   a plan code we cannot classify yet      ← unknown + a base_type we could not decode
 *
 * ⚠️ TWO of the seven collapse in a way that loses a distinction the old badges carried. BOTH are
 * DELIBERATE product decisions (คุณใหม่, 2026-07-15), not oversights:
 *
 *   • paid_expired → "Expired". A paying customer Zoom wrongly expired (QUALICA, four years paid) now
 *     reads plain "Expired" like anyone else; the "verify, don't write them off" nuance the old red badge
 *     carried is gone from the portal. The protection that remains: these accounts stay FINDABLE (never
 *     hidden from search / 360 / counts — that filter lives in SQL and is untouched), and the incident
 *     detail lives in the backend v_plan_incident view. Do not re-hide them here.
 *
 *   • workaround_trial → "Active". These ~4 accounts (Walailak, Mahachula, Burapha, Princess Galyani) paid
 *     and renewed, Zoom wrongly expired the real plan, and a temporary trial-plan was patched over the top
 *     so they keep working. That temporary plan is GENUINELY LIVE (Zoom answers /plans/usage, seats are
 *     current), so the honest simple status is "Active". The principal chose this over "Expired" knowing it
 *     reads like a healthy account and shows NO badge in compact/search: the portal is a monitor, and the
 *     open-Zoom-incident detail is a backend concern (v_plan_incident), no longer a portal badge. Do not
 *     "restore" a distinct incident treatment here without re-asking — it was considered and declined.
 *
 * base_type IS NULL ⟺ the Free set (verified on prod 2026-07-09: 820 accounts, all with zero rows in
 * sub_account_plans). So an `unknown` plan_state with no base_type is Free, not a mystery; an `unknown`
 * plan_state WITH a base_type is a genuine opaque code (e.g. PLANCODE_1127) and stays "Unknown".
 *
 * ── 2026-09-18 (API migration 0030): the 2026-07-15 collapse below is now the FALLBACK ──────────────
 * The backend's `v_account_status` view computes a real VERDICT — `plan_badge` — from the principal's
 * 2026-09-17 ruling (FREE if `account_type LIKE 'Free (%'`; ACTIVE if any subscription line is still
 * valid; EXPIRED otherwise), which is strictly more informed than the plan_state+base_type collapse
 * ever was: it reads the actual subscription lines, not just the base-plan row. When `plan_badge` is
 * present it WINS OUTRIGHT — see the whitelist below — and the switch that follows runs ONLY for an
 * old/stripped payload that omits it (old-API skew), producing exactly the value it always did. This is
 * why the whitelist check comes FIRST and returns immediately: the 2026-07-15 rule is superseded, not
 * deleted, and every fixture/test that predates 0030 keeps working unchanged through the fallback path.
 */

import type { PlanBadge, PlanState } from "@/api/types";
import { planStateOf } from "./planState";

export type AccountStatus = "active" | "free" | "expired" | "unknown";

const PLAN_BADGE_VALUES = new Set<PlanBadge>(["active", "free", "expired", "unknown"]);

/**
 * Derive the single simple status from plan_state + base_type — or, when the server sent one, straight
 * from the 0030 ruling's own verdict (`plan_badge`). UNIFORM across every surface (header, overview,
 * search, tables) so one account can never show two different statuses.
 *
 * `plan_badge` is a literal WHITELIST check, never re-derived: exactly "active" | "free" | "expired" |
 * "unknown" returns that value as-is; anything else (absent, null, an unrecognised string a future backend
 * adds) falls through, BYTE-IDENTICAL, to the switch below. That switch fails SAFE exactly as planStateOf
 * does: an absent / null / unrecognised plan_state normalises to `unknown`, never to `expired`.
 *
 * ⚠️ 2026-09-18 (round 3, ruling 3): a server-sent `plan_badge: "unknown"` and the FALLBACK's own
 * `unknown` verdict (an opaque plan code the switch below could not classify) land on the SAME return
 * value but mean DIFFERENT things — "we have no plan data for this account at all" vs "we have a plan
 * code we cannot decode" — and AccountStatusBadge shows a different hint for each. This function does not
 * disambiguate them in its return value (both are the string `"unknown"`, by design — one AccountStatus);
 * the caller distinguishes by checking `plan_badge === "unknown"` directly, the same way it already has
 * the raw badge in hand.
 *
 * The switch is EXHAUSTIVE over PlanState on purpose (mirroring ALL_STATES in planState.ts, built after a
 * new state once fell through to `unknown` and hid a $10,364 incident): add an 8th PlanState without
 * handling it here and the `never` assignment below stops compiling. The runtime fallback never throws and
 * never accuses a live customer of being expired.
 */
export function accountStatusOf(
  plan_state: unknown,
  base_type: unknown,
  plan_badge?: unknown,
): AccountStatus {
  if (typeof plan_badge === "string" && PLAN_BADGE_VALUES.has(plan_badge as PlanBadge)) {
    return plan_badge as PlanBadge;
  }

  const s: PlanState = planStateOf(plan_state);
  switch (s) {
    case "live":
    case "workaround_trial":
      return "active";
    case "paid_expired":
    case "lapsed_trial":
    case "cancelled":
    case "expired_other":
      return "expired";
    case "unknown": {
      // no base-plan row → Free; a base_type we could not classify → Unknown.
      const hasBase = typeof base_type === "string" && base_type.trim().length > 0;
      return hasBase ? "unknown" : "free";
    }
  }
  // Unreachable today (planStateOf returns one of the 7 handled above). COMPILE-TIME guard against an 8th
  // PlanState; the runtime return is the fail-safe direction.
  const _exhaustive: never = s;
  void _exhaustive;
  return "unknown";
}

/**
 * True when the SERVER's `free` verdict (`plan_badge`) OVERRODE what plan_state + base_type ALONE would
 * have said — i.e. the badge disagrees with the pre-0030 collapse. QUALICA / G STEEL / GJ Steel are the
 * real shape: a genuine historical base plan (`paid_expired` / `live`), but "Free (with credit card)" on
 * `account_type` wins under the ruling. Their seats / seats_last_known / renewal_date are then ZOMBIE —
 * leftovers of a plan the ruling says does not apply — and every surface that would otherwise print them
 * as a current OR a last-known fact must suppress them instead. An account that already read Free under
 * the OLD collapse (no base row at all — the ordinary ~892) returns false here: nothing disagrees with
 * plan_state/base_type, so there is nothing to suppress.
 *
 * SHARED (round 2, W2) by CustomerHeader (the seats / seats-last-known / renewal-date facts in the header
 * meta row) and OverviewTab's Kpis (the Renewal tile and the Seats / "Seats (last known)" tile) — the
 * ONLY two gates on this axis, so the two surfaces can never disagree with each other. Deliberately NOT
 * wired into `isPlanNotLive` / `PlanFreezeNotice` — a different, intentionally independent axis the two
 * CAN disagree with (open product question; do not fold them together without re-asking).
 *
 * base_type-INDEPENDENCE (round 2, W3) — ⚠️ CORRECTED (round 3 carry-over C): the claim used to be that
 * item 3 (Free-header coherence — NULLS `base_type` for a Free-badged account) never changes this
 * function's verdict. That holds for the plan_state values this build actually disagrees on (`paid_expired`,
 * `live` — QUALICA/G STEEL/GJ Steel), which the fallback switch matches by plan_state ALONE, before
 * base_type is ever consulted. It does NOT hold for the `unknown` branch of that switch: REFUTED —
 * item 3's null-out DISARMS the gate for the cohort where plan_state normalises to `unknown` (absent,
 * null, or an unrecognised value). With item 3 having run (`base_type` nulled), the fallback's `unknown`
 * branch reads `hasBase=false` → "free", which MATCHES the badge → `isZombieFreeAccount` returns false.
 * Without item 3 (base_type still the real, superseded plan), the same branch reads `hasBase=true` →
 * "unknown", which DISAGREES with the badge → this function returns TRUE instead. So for that one cohort
 * the verdict FLIPS depending on whether item 3 has run — the opposite of "same either way". 0 accounts
 * carry this shape on the local mirror today (a Free-badged account with an `unknown`-normalising
 * plan_state), so it is a KNOWN FAIL-OPEN, pinned by name in accountStatus.test.ts, rather than an observed
 * bug: a future change to either side of this gate is now a decision, not an accident. Tested both ways for
 * the real cohorts (`paid_expired`/`live`): fixtures/tests cover both the 0030-with-item-3 shape
 * (base_type nulled) and the 0030-without-item-3 deploy skew (base_type still the real, superseded plan) —
 * see `customerOverviewFreeBadgeBaseTypeStillPresent` in mock/fixtures.ts.
 */
export function isZombieFreeAccount(
  plan_state: unknown,
  base_type: unknown,
  plan_badge?: unknown,
): boolean {
  const status = accountStatusOf(plan_state, base_type, plan_badge);
  const fallback = accountStatusOf(plan_state, base_type);
  return status === "free" && fallback !== "free";
}

/**
 * W8-Q1 (owner ruling 2026-09-19, "ทำ") — does this account show a Renewal date AT ALL?
 *
 * No, whenever the account reads Free. Zoom prints only "Basic Plan (Free)" for a Free account — no
 * dates — and PTG Energy 57148993 (Free badge) printed "Renewal Jun 23, 2027 · in 277 days" in the header
 * and the Overview Renewal tile off the last snapshot of a plan that had ended. `isZombieFreeAccount` did
 * not catch it: PTG's plan_state is `unknown` and the API nulls base_type for a Free account, so the
 * fallback also reads "free" and nothing "disagrees" (the KNOWN FAIL-OPEN pinned in accountStatus.test.ts).
 *
 * DELIBERATELY BROADER than `isZombieFreeAccount`, and ONLY for the renewal fact. For SEATS the broad rule
 * `status === "free"` was rejected (ABCD Logistics' live Basic seats must still show); for the RENEWAL the
 * owner ruled the opposite — a Free account has no renewal, whatever row is left behind. So the two gates
 * are separate on purpose; do not fold seats into this one.
 *
 * The SAME status every surface shows (`accountStatusOf`): the server's plan_badge when present, which
 * the API computes with the same A1 predicate it uses to null renewal_date (service._overview_renewal).
 * The API already sends no date for these accounts; this gate is what hides the Overview TILE (it would
 * otherwise print "Renewal —") and keeps an old API's date off the page.
 *
 * SHARED by CustomerHeader (the Renewal fact) and OverviewTab's Kpis (the Renewal tile).
 *
 * KNOWN FAIL-CLOSED (latent): with NO plan_badge (an API older than 0030, or no v_account_status row —
 * which the API says cannot happen, the view selects FROM sub_accounts), the fallback reads
 * plan_state `unknown` + no base_type as "free", so a base-less paid account would lose its renewal
 * fact and tile. Prod sends plan_badge on every account, so nothing is hidden wrongly today.
 */
export function showsRenewal(
  plan_state: unknown,
  base_type: unknown,
  plan_badge?: unknown,
): boolean {
  return accountStatusOf(plan_state, base_type, plan_badge) !== "free";
}
